/**
 * Seat-limit UI for the web portal.
 *
 * When a studio has more members than its plan allows, the server
 * starts a 7-day grace period (`seatLimit.graceEndsAt`); afterwards
 * members above the cap are paused (`pausedMemberIds`). See
 * `functions/src/billing/seatLimit.ts`. Plans and seat choices are
 * managed in the mobile app, so these notices point there.
 */
import { useMemo } from 'react';

import { isSuperAdmin } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { tierLabel } from '@/lib/billingPlans';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';

import { AlertBanner } from './page/AlertBanner';

export type WebSeatStatus = {
  /** Project limit: grace running or projects read-only. */
  projectsOverLimit: boolean;
  projectGraceEndsAtMs: number | null;
  lockedProjectIds: string[];
  isPaused: boolean;
  overLimit: boolean;
  enforced: boolean;
  pausedCount: number;
  graceEndsAtMs: number | null;
  orgName: string;
  planLabel: string;
};

export function useWebSeatStatus(): WebSeatStatus {
  const { user } = useAuth();
  const { org } = useFullOrganization();
  return useMemo(() => {
    const d = (org?.data ?? {}) as {
      name?: string;
      ownerId?: string;
      pausedMemberIds?: string[];
      seatLimit?: { graceEndsAt?: { toMillis(): number } } | null;
      projectLimit?: { graceEndsAt?: { toMillis(): number } } | null;
      lockedProjectIds?: string[];
      subscription?: { tier?: string };
    };
    const paused = Array.isArray(d.pausedMemberIds) ? d.pausedMemberIds : [];
    return {
      projectsOverLimit: !!d.projectLimit,
      projectGraceEndsAtMs: d.projectLimit?.graceEndsAt?.toMillis() ?? null,
      lockedProjectIds: Array.isArray(d.lockedProjectIds) ? d.lockedProjectIds : [],
      isPaused: !!user && d.ownerId !== user.uid && paused.includes(user.uid),
      overLimit: !!d.seatLimit,
      enforced: paused.length > 0,
      pausedCount: paused.length,
      graceEndsAtMs: d.seatLimit?.graceEndsAt?.toMillis() ?? null,
      orgName: d.name ?? 'This studio',
      planLabel: tierLabel(d.subscription?.tier ?? 'free'),
    };
  }, [org, user]);
}

function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Replaces the page content for a member whose seat is paused. */
export function SeatPausedNotice({ status }: { status: WebSeatStatus }) {
  return (
    <div className="page active" style={{ display: 'block' }}>
      <div className="card" style={{ padding: 32, textAlign: 'center', maxWidth: 560, margin: '48px auto' }}>
        <div style={{ fontSize: 36, marginBottom: 8, color: 'var(--warning-text)' }}>
          <i className="ph ph-pause-circle" />
        </div>
        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>Your access is paused</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
          {status.orgName}&apos;s current plan has fewer team seats than the studio has members,
          so your seat is paused for now. Nothing has been deleted — ask the studio owner to
          upgrade the plan or give you a seat, and your access comes back straight away. You can
          switch to another studio from the studio menu in the sidebar.
        </div>
      </div>
    </div>
  );
}

/** Warning for owners / admins while the studio is over its seat cap. */
export function SeatLimitNotice({ status }: { status: WebSeatStatus }) {
  const { role } = useCurrentOrgRole();
  if (!status.overLimit || !role || role === 'client') return null;
  const who = isSuperAdmin(role) ? 'Open' : 'Ask the studio owner to open';

  return (
    <div style={{ marginBottom: 16 }}>
      <AlertBanner
        tone="warning"
        title={
          status.enforced
            ? `${status.pausedCount} member${status.pausedCount === 1 ? '' : 's'} paused`
            : `Choose who keeps access by ${fmtDate(status.graceEndsAtMs)}`
        }
        body={
          status.enforced
            ? `Your studio has more members than the ${status.planLabel} plan includes, so extra members are paused. ${who} the InteriorIO mobile app → Account → Plans to upgrade, or Team & roles to change who has a seat.`
            : `Your studio has more members than the ${status.planLabel} plan includes. After ${fmtDate(status.graceEndsAtMs)}, members without a seat are paused (nothing is deleted). ${who} the InteriorIO mobile app → Account → Plans to upgrade, or Team & roles to choose who stays.`
        }
      />
    </div>
  );
}

/** Studio-level warning while the studio is over its project cap. */
export function ProjectLimitNotice({ status }: { status: WebSeatStatus }) {
  const { role } = useCurrentOrgRole();
  if (!status.projectsOverLimit || !role || role === 'client') return null;
  const locked = status.lockedProjectIds.length;
  const who = isSuperAdmin(role) ? 'Open' : 'Ask the studio owner to open';
  return (
    <div style={{ marginBottom: 16 }}>
      <AlertBanner
        tone="warning"
        title={
          locked > 0
            ? `${locked} project${locked === 1 ? ' is' : 's are'} view-only`
            : `Older projects become view-only on ${fmtDate(status.projectGraceEndsAtMs)}`
        }
        body={
          locked > 0
            ? `Your studio has more projects than the ${status.planLabel} plan includes. Your most recent projects stay editable; older ones can be viewed but not changed. ${who} the InteriorIO mobile app → Account → Plans to upgrade, or delete projects you no longer need.`
            : `Your studio has more projects than the ${status.planLabel} plan includes. After ${fmtDate(status.projectGraceEndsAtMs)}, only your most recent projects stay editable — older ones become view-only. Nothing is deleted. ${who} the InteriorIO mobile app → Account → Plans to upgrade.`
        }
      />
    </div>
  );
}

/** Banner inside a project that is read-only on the current plan. */
export function ProjectReadOnlyNotice({ projectId }: { projectId: string }) {
  const status = useWebSeatStatus();
  if (!status.lockedProjectIds.includes(projectId)) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <AlertBanner
        tone="warning"
        title="View-only project"
        body={`Your ${status.planLabel} plan includes a limited number of active projects — your most recent ones. You can view everything in this project, but changes won't save until the studio upgrades in the InteriorIO mobile app.`}
      />
    </div>
  );
}
